import { HttpResponse } from '@angular/common/http';
import { Injectable, signal } from '@angular/core';
import { map, Observable } from 'rxjs';
import { ApiResponsePaged } from '@core/interfaces/api.interface';
import { DeleteValidationResult } from '@core/interfaces/delete-validation.interface';
import { BaseService } from '@core/services/base.service';
import { mapPagedResponse } from '@core/utils/mapper-response-paged.util';
import { MaintenanceWork, MaintenanceWorkFilter, MaintenanceWorkForm } from '@features/advanced-maintenance/interfaces/maintenance.interface';

@Injectable({ providedIn: 'root' })
export class MaintenanceWorksService extends BaseService {

  currentWork = signal<MaintenanceWork | null>(null);

  constructor() {
    super('MaintenanceWorks');
  }

  getAll(params: MaintenanceWorkFilter): Observable<ApiResponsePaged<MaintenanceWork>> {
    return this.GET<HttpResponse<MaintenanceWork[]>>('', params, {
      observe: 'response',
    }).pipe(mapPagedResponse<MaintenanceWork>());
  }
  
  getById(id: string): Observable<MaintenanceWork> {
    return this.GET<MaintenanceWork>(id).pipe(
      map((work) => {
        this.currentWork.set(work);
        return work;
      })
    );
  }

  create(payload: Omit<MaintenanceWorkForm, 'id' | 'maintenanceRoutineId'>): Observable<MaintenanceWork> {
    return this.POST<MaintenanceWork>('', payload);
  }

  update(id: string, payload: Omit<MaintenanceWorkForm, 'maintenanceRoutineId' | 'maintenanceRoutineWork'>): Observable<MaintenanceWork> {
    return this.PUT<MaintenanceWork>(id, payload);
  }

  delete(id: string): Observable<void> {
    return this.DELETE<void>(id);
  }

  /**
   * @description
   * Verifica si el trabajo puede eliminarse o si tiene referencias en otras tablas que lo impiden.
   * @param {string} id Identificador del trabajo.
   * @returns {Observable<DeleteValidationResult>}
   */
  getDeleteValidation(id: string): Observable<DeleteValidationResult> {
    return this.GET<DeleteValidationResult>(`${id}/delete-validation`);
  }

  /**
   * @description
   * Verifica si ya existe un trabajo con el mismo nombre en el negocio.
   * @param {object} params Parámetros de la validación.
   * @param {string} params.businessId Identificador del negocio.
   * @param {string} params.name Nombre a validar.
   * @param {string} [params.excludeId] Identificador del trabajo a excluir (edición).
   * @returns {Observable<boolean>} `true` si el nombre ya está en uso.
   */
  nameExists(params: { businessId: string; name: string; excludeId?: string }): Observable<boolean> {
    return this.GET<{ hasDuplicate: boolean }>('name-exists', params).pipe(
      map((response) => response.hasDuplicate),
    );
  }

  export(params: MaintenanceWorkFilter): Observable<Blob> {
    return this.GET<Blob>('excel', params, {
      responseType: 'blob',
    });
  }

  bulkUpload(businessId: string, file: File): Observable<HttpResponse<Blob>> {
    const formData = new FormData();
    formData.append('file', file);
    const url = `${this.apiUrl}/${this.endpoint}/upload/${businessId}`;
    return this._http.post(url, formData, {
      responseType: 'blob',
      observe: 'response',
    });
  }
}
